import React, {FC} from 'react';
import styled from 'styled-components';

import {hoverSelector} from '../../../../../libs/shared/helpers/src/browser/hoverHelpers';
import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {useToggle} from '../../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
// See https://github.com/frontapp/front-client/pull/4831/files#r517976628
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {DropdownSeparator} from '../../../../components/common/dropdowns/dropdownSeparator';
import {Pill} from '../../../../components/pill/pill';
import {useAppDispatch, useAppSelector} from '../../../../helpers/reduxHelpers';
import {InterfaceEventsEnum} from '../../../../services/tracking';
import {
  getCompanyHasAdvancedAnalytics,
  getCompanyHasAnalyticsScheduledReports,
} from '../../../../store/data/company/companySelectors';
import {getAnalyticsPricingFeatureFlag} from '../../../../store/featureFlags/featureFlagsSelectors';
import {getAnalyticsParameters} from '../../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {createSavedViewAsyncAction} from '../../../../store/panels/analytics/views/analyticsPanelViewsActions';
import {makeGetSelectedView} from '../../../../store/panels/analytics/views/analyticsPanelViewsSelectors';
import {
  ALL_DATA_ID,
  AnalyticsSavedView,
  AnalyticsSelectedViewId,
  isAnalyticsViewSaved,
} from '../../../../store/panels/analytics/views/analyticsPanelViewsState';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {makeAnalyticsSelectedViewUrl} from '../../../helpers/analyticsUrlHelpers';
import {useAnalyticsTracking} from '../../../helpers/tracking/useAnalyticsTracking';
import {AnalyticsExportDialog} from '../../exports/analyticsExportDialog';
import {StyledFlex} from '../../reportViewer/reportCommons';
import {ReportHeaderDeleteViewDialog} from './dialogs/reportHeaderDeleteViewDialog';
import {ReportHeaderSaveViewDialog} from './dialogs/reportHeaderSaveViewDialog';
import {ReportHeaderScheduleViewDialog} from './dialogs/reportHeaderScheduleViewDialog';

/*
 * Props.
 */

interface ReportHeaderFiltersMoreButtonProps {
  selectedViewId: AnalyticsSelectedViewId;
}

/*
 * Styles.
 */

const StyledPricingDiamondIcon = styled(Icon)`
  margin-left: 4px;
`;

const StyledPrimePill = styled(Pill)`
  margin-left: 4px;
`;

/*
 * Component.
 */

const renderEditViewItemText = (
  selectedViewId: AnalyticsSelectedViewId,
  selectedView: AnalyticsSavedView | undefined,
  toggleScheduleViewDialog: Handler,
  hasAdvancedAnalytics: boolean,
  hasAnalyticsScheduledReports: boolean,
  hasAnalyticsPricingFeatureFlag: boolean,
) => {
  if (!isAnalyticsViewSaved(selectedViewId)) {
    return null;
  }

  if (
    // Scheduled report is enabled if the FF is enabled AND the scheduled report access is enabled.
    (hasAnalyticsPricingFeatureFlag && hasAnalyticsScheduledReports) ||
    // Scheduled report is enabled if the FF is disabled AND the advanced analytics access is enabled.
    (!hasAnalyticsPricingFeatureFlag && hasAdvancedAnalytics)
  ) {
    return (
      <SimpleDropdownItem onSelect={toggleScheduleViewDialog}>
        {selectedView?.schedulingConfig ? (
          <Trans id="analytics-view-dropdown-edit-schedule">Edit scheduled report</Trans>
        ) : (
          <Trans id="analytics-view-dropdown-schedule">Schedule report</Trans>
        )}
      </SimpleDropdownItem>
    );
  }

  // Scheduled report is disabled if the FF is enabled AND the scheduled report access is disabled.
  // We'll display the new pricing icon (diamond).
  if (hasAnalyticsPricingFeatureFlag && !hasAnalyticsScheduledReports) {
    return (
      <SimpleDropdownItem isDisabled>
        <StyledFlex>
          <Trans id="analytics-view-dropdown-schedule">Schedule report</Trans>
          <UserThemeConsumer>
            {(theme) => (
              <StyledPricingDiamondIcon name="diamondSmall" size={20} color={theme.palette.yellow.shade40} />
            )}
          </UserThemeConsumer>
        </StyledFlex>
      </SimpleDropdownItem>
    );
  }

  // Scheduled report is disabled if the FF is disabled AND the advanced analytics access is disabled.
  // We'll display the legacy icon (pill with growth plan label).
  return (
    <SimpleDropdownItem isDisabled>
      <StyledFlex>
        <Trans id="analytics-view-dropdown-schedule">Schedule report</Trans>
        <StyledPrimePill size={VisualSizesEnum.MEDIUM}>
          <Trans id="upsell-pill-growth">GROWTH</Trans>
        </StyledPrimePill>
      </StyledFlex>
    </SimpleDropdownItem>
  );
};

const renderEditViewItems = (
  selectedViewId: AnalyticsSelectedViewId,
  selectedView: AnalyticsSavedView | undefined,
  onDuplicateClick: Handler,
  toggleUpdateViewDialog: Handler,
  toggleDeleteViewDialog: Handler,
  toggleScheduleViewDialog: Handler,
  toggleExportViewDialog: Handler,
  hasAdvancedAnalytics: boolean,
  hasAnalyticsScheduledReports: boolean,
  hasAnalyticsPricingFeatureFlag: boolean,
) => (
  <>
    {maybeDisplayEditViewActions(
      selectedViewId,
      toggleUpdateViewDialog,
      onDuplicateClick,
      toggleDeleteViewDialog,
    )}

    {renderEditViewItemText(
      selectedViewId,
      selectedView,
      toggleUpdateViewDialog,
      hasAdvancedAnalytics,
      hasAnalyticsScheduledReports,
      hasAnalyticsPricingFeatureFlag,
    )}

    <SimpleDropdownItem onSelect={toggleExportViewDialog}>
      <Trans id="analytics-view-dropdown-export">Export view data</Trans>
    </SimpleDropdownItem>
  </>
);

export const ReportHeaderFiltersMoreButton: FC<ReportHeaderFiltersMoreButtonProps> = ({selectedViewId}) => {
  const dispatch = useAppDispatch();

  const [shouldShowDeleteViewDialog, toggleDeleteViewDialog] = useToggle();
  const [shouldShowUpdateViewDialog, toggleUpdateViewDialog] = useToggle();
  const [shouldShowScheduleViewDialog, toggleScheduleViewDialog] = useToggle();
  const [shouldShowExportViewDialog, toggleExportViewDialog] = useToggle();

  const currentSavedView = useAppSelector(makeGetSelectedView(selectedViewId));
  const hasAdvancedAnalytics = useAppSelector(getCompanyHasAdvancedAnalytics);
  const hasAnalyticsScheduledReports = useAppSelector(getCompanyHasAnalyticsScheduledReports);
  const hasAnalyticsPricingFeatureFlag = useAppSelector(getAnalyticsPricingFeatureFlag);

  const parameters = useAppSelector(getAnalyticsParameters);

  const trackAnalytics = useAnalyticsTracking();

  const onEditViewClick = () => {
    trackAnalytics(InterfaceEventsEnum.ANALYTICS_EDIT_VIEW_CLICK);
    toggleUpdateViewDialog();
  };

  const onDeleteViewDialogConfirm = () => {
    // Redirect to "All Data" tab after deletion
    const path = makeAnalyticsSelectedViewUrl(ALL_DATA_ID, parameters);
    dispatch(navigateToLinkAction(path));
    toggleDeleteViewDialog();
  };

  const onUpdateViewDialogConfirm = () => {
    trackAnalytics(InterfaceEventsEnum.ANALYTICS_VIEW_EDIT);
    // TODO update store
    toggleUpdateViewDialog();
  };

  const onDuplicateClick = async () => {
    if (!currentSavedView?.namespace) {
      return;
    }

    // TODO navigation should be triggered in createSavedViewAsyncAction and updateSavedViewAsyncAction
    // after cleaning circular dependencies in views action.
    const savedViewId: unknown = await dispatch(
      createSavedViewAsyncAction(
        {
          ...currentSavedView,
          name: `${currentSavedView.name} (Copy)`,
        },
        currentSavedView.namespace,
      ),
    );

    if (typeof savedViewId === 'number') {
      const path = makeAnalyticsSelectedViewUrl(savedViewId, parameters);
      dispatch(navigateToLinkAction(path));
    }
  };

  const renderDropdown = (popoverProps: PopoverTransferProps) => (
    <SimpleDropdown {...popoverProps} width={225} placement="bottom-end">
      {renderEditViewItems(
        selectedViewId,
        currentSavedView,
        onDuplicateClick,
        onEditViewClick,
        toggleDeleteViewDialog,
        toggleScheduleViewDialog,
        toggleExportViewDialog,
        hasAdvancedAnalytics,
        hasAnalyticsScheduledReports,
        hasAnalyticsPricingFeatureFlag,
      )}
    </SimpleDropdown>
  );

  return (
    <div>
      <PopoverCoordinator renderButton={renderButton} renderModal={renderDropdown} shouldHoverWhenOpen />
      {/* Delete Dialog */}
      {shouldShowDeleteViewDialog && (
        <ReportHeaderDeleteViewDialog
          onOkClick={onDeleteViewDialogConfirm}
          onRequestClose={toggleDeleteViewDialog}
          selectedViewId={selectedViewId}
        />
      )}
      {/* Save Dialog */}
      {shouldShowUpdateViewDialog && (
        <ReportHeaderSaveViewDialog
          onOkClick={onUpdateViewDialogConfirm}
          onRequestClose={toggleUpdateViewDialog}
          selectedView={currentSavedView}
        />
      )}
      {/* Schedule Dialog */}
      {shouldShowScheduleViewDialog && (
        <ReportHeaderScheduleViewDialog
          onOkClick={toggleScheduleViewDialog}
          onRequestClose={toggleScheduleViewDialog}
          selectedViewId={selectedViewId}
        />
      )}
      {/* Export Dialog */}
      {shouldShowExportViewDialog && <AnalyticsExportDialog onRequestClose={toggleExportViewDialog} />}
    </div>
  );
};

/*
 * Helpers.
 */

const StyledIconContainer = styled.div`
  border-radius: 8px;
  ${hoverSelector} {
    background-color: ${(p) => p.theme.alphas.gray10};
  }
`;

const renderButton = () => (
  <UserThemeConsumer>
    {(theme) => (
      <StyledIconContainer>
        <IconButton
          iconName="more"
          label={<Trans id="analytics-more-button">More</Trans>}
          color={theme.greys.shade70}
          hoverColor={theme.greys.shade80}
          backgroundHoverColor="transparent" // Let StyledIconContainer handle the hover background.
        />
      </StyledIconContainer>
    )}
  </UserThemeConsumer>
);

function maybeDisplayEditViewActions(
  selectedViewId: AnalyticsSelectedViewId,
  toggleUpdateViewDialog: Handler,
  onDuplicateClick: Handler,
  toggleDeleteViewDialog: Handler,
) {
  if (isAnalyticsViewSaved(selectedViewId)) {
    return (
      <>
        <SimpleDropdownItem onSelect={toggleUpdateViewDialog}>
          <Trans id="analytics-view-dropdown-edit">Edit view</Trans>
        </SimpleDropdownItem>
        <SimpleDropdownItem onSelect={onDuplicateClick}>
          <Trans id="analytics-view-dropdown-duplicate">Duplicate view</Trans>
        </SimpleDropdownItem>
        <SimpleDropdownItem onSelect={toggleDeleteViewDialog}>
          <Trans id="analytics-view-dropdown-delete">Delete view</Trans>
        </SimpleDropdownItem>

        <DropdownSeparator />
      </>
    );
  }

  return null;
}
